import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
    Calendar, Clock, User, Bookmark, ChevronRight,
    Home, Tag, TrendingUp, MessageCircle, Eye, Send,
    Facebook, Twitter, Mail, Check, ArrowLeft, Link as LinkIcon, ThumbsUp
} from 'lucide-react'
import { useLanguageStore, useAuthStore } from '../../lib/store'
import { newsApi } from '../../lib/api'
import { formatDate } from '../../lib/utils'

interface Article {
    id: number
    slug: string
    title_en: string
    title_vi: string
    excerpt_en?: string
    excerpt_vi?: string
    content_en: string
    content_vi: string
    category: string
    featured_image?: string
    author_name?: string
    published_at?: string
    created_at?: string
    read_time?: string
    views?: number
}

interface Category {
    slug: string
    name_en: string
    name_vi: string
    icon: string
}

interface Comment {
    id: number
    content: string
    guest_name?: string
    user_name?: string
    avatar_url?: string
    created_at: string
    likes: number
    replies?: Comment[]
}

interface LayoutConfig {
    layout_style: 'classic' | 'modern' | 'magazine' | 'minimal'
    hero_style: 'overlay' | 'above' | 'minimal'
    sidebar_config: { sections: { id: string; type: string; enabled: boolean; title_vi: string; title_en: string }[] }
    show_comments: boolean
    show_share: boolean
}

export default function NewsDetailPage() {
    const { slug } = useParams()
    const { language } = useLanguageStore()
    const { user } = useAuthStore()

    const [article, setArticle] = useState<Article | null>(null)
    const [relatedArticles, setRelatedArticles] = useState<Article[]>([])
    const [categories, setCategories] = useState<Category[]>([])
    const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null)
    const [comments, setComments] = useState<Comment[]>([])
    const [loading, setLoading] = useState(true)
    const [copied, setCopied] = useState(false)
    const [saved, setSaved] = useState(false)

    // Comment form
    const [commentForm, setCommentForm] = useState({ name: '', email: '', content: '' })
    const [submitting, setSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    useEffect(() => {
        Promise.all([
            fetch('/api/categories').then(r => r.json()),
            fetch('/api/admin/article-layout').then(r => r.json()).catch(() => null)
        ]).then(([catData, configData]) => {
            setCategories(catData.categories || [])
            if (configData?.success) setLayoutConfig(configData.config)
        })
    }, [])

    useEffect(() => {
        const fetchArticle = async () => {
            if (!slug) return
            setLoading(true)
            try {
                const response = await newsApi.get(slug)
                if (response.data?.data) {
                    const art = response.data.data
                    setArticle(art)

                    // Fetch related articles
                    const relatedRes = await newsApi.list({ category: art.category, limit: 4 })
                    setRelatedArticles((relatedRes.data?.data || []).filter((a: Article) => a.slug !== slug).slice(0, 3))

                    // Fetch comments
                    try {
                        const commentsRes = await fetch(`/api/comments/${art.id}`)
                        const commentsData = await commentsRes.json()
                        if (commentsData.success) setComments(commentsData.comments || [])
                    } catch { }
                }
            } catch (error) {
                console.error('Failed to fetch article:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchArticle()
    }, [slug])

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const handleShare = (platform: string) => {
        const url = encodeURIComponent(window.location.href)
        const shareTitle = encodeURIComponent(article ? (language === 'en' ? article.title_en : article.title_vi) : '')
        const urls: Record<string, string> = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}&text=${shareTitle}`,
            email: `mailto:?subject=${shareTitle}&body=${url}`
        }
        if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400')
    }

    const handleSubmitComment = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!article || !commentForm.content.trim()) return

        setSubmitting(true)
        try {
            const res = await fetch(`/api/comments/${article.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: commentForm.content,
                    guest_name: user?.fullName || commentForm.name,
                    guest_email: user?.email || commentForm.email,
                    user_id: user?.id
                })
            })
            const data = await res.json()
            if (data.success) {
                setSubmitSuccess(true)
                setCommentForm({ name: '', email: '', content: '' })
                setTimeout(() => setSubmitSuccess(false), 3000)
            }
        } catch (error) {
            console.error('Submit comment error:', error)
        } finally {
            setSubmitting(false)
        }
    }

    const config = layoutConfig || {
        layout_style: 'modern',
        hero_style: 'overlay',
        sidebar_config: { sections: [] },
        show_comments: true,
        show_share: true
    }

    const showSidebar = config.layout_style === 'classic' || config.layout_style === 'modern'

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="h-48 md:h-64 bg-gray-200 animate-pulse" />
                <div className="container mx-auto px-4 py-6">
                    <div className="max-w-3xl mx-auto space-y-4">
                        <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse" />
                        <div className="h-48 bg-gray-200 rounded animate-pulse" />
                    </div>
                </div>
            </div>
        )
    }

    if (!article) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
                <h1 className="text-xl font-bold mb-4 text-center">{language === 'en' ? 'Article Not Found' : 'Không tìm thấy bài viết'}</h1>
                <Link to="/blog" className="text-primary-600 hover:underline">{language === 'en' ? 'Back to Blog' : 'Quay lại Blog'}</Link>
            </div>
        )
    }

    const title = language === 'en' ? article.title_en : (article.title_vi || article.title_en)
    const excerpt = language === 'en' ? (article.excerpt_en || '') : (article.excerpt_vi || article.excerpt_en || '')
    const content = language === 'en' ? article.content_en : (article.content_vi || article.content_en)
    const currentCategory = categories.find(c => c.slug === article.category)
    const getCatName = (c?: Category) => c ? (language === 'en' ? c.name_en : c.name_vi) : ''
    const getArtTitle = (a: Article) => language === 'en' ? a.title_en : (a.title_vi || a.title_en)

    const enabledSections = config.sidebar_config?.sections?.filter(s => s.enabled) || [
        { id: 'related', type: 'related', enabled: true, title_vi: 'Bài viết liên quan', title_en: 'Related Articles' },
        { id: 'categories', type: 'categories', enabled: true, title_vi: 'Danh mục', title_en: 'Categories' },
        { id: 'cta', type: 'cta', enabled: true, title_vi: 'Đặt Tour', title_en: 'Book a Tour' },
        { id: 'newsletter', type: 'newsletter', enabled: true, title_vi: 'Nhận tin', title_en: 'Newsletter' }
    ]

    const renderSidebar = () => (
        <div className="space-y-4">
            {enabledSections.map(section => {
                if (section.type === 'related' && relatedArticles.length > 0) {
                    return (
                        <div key={section.id} className="bg-white rounded-lg shadow-sm p-4">
                            <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <Tag className="w-3.5 h-3.5" />
                                {language === 'en' ? section.title_en : section.title_vi}
                            </h3>
                            <div className="space-y-3">
                                {relatedArticles.map(a => (
                                    <Link key={a.id} to={`/blog/${a.category}/${a.slug}`} className="flex gap-3 group">
                                        <img src={a.featured_image || 'https://via.placeholder.com/80x60'} alt="" className="w-14 h-10 rounded object-cover flex-shrink-0" />
                                        <h4 className="text-sm line-clamp-2 group-hover:text-primary-600 transition">{getArtTitle(a)}</h4>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )
                }
                if (section.type === 'categories') {
                    return (
                        <div key={section.id} className="bg-white rounded-lg shadow-sm p-4">
                            <h3 className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <TrendingUp className="w-3.5 h-3.5" />
                                {language === 'en' ? section.title_en : section.title_vi}
                            </h3>
                            <div className="space-y-1">
                                {categories.slice(0, 5).map(cat => (
                                    <Link key={cat.slug} to={`/blog/${cat.slug}`} className={`block px-3 py-1.5 rounded text-sm ${cat.slug === article.category ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}`}>{getCatName(cat)}</Link>
                                ))}
                            </div>
                        </div>
                    )
                }
                if (section.type === 'cta') {
                    return (
                        <div key={section.id} className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg p-4 text-white">
                            <h3 className="font-bold text-sm mb-1">{language === 'en' ? 'Plan Your Trip' : 'Lên kế hoạch'}</h3>
                            <p className="text-xs text-white/80 mb-3">{language === 'en' ? 'Discover tours' : 'Khám phá tour'}</p>
                            <Link to="/tours" className="inline-block px-3 py-1.5 bg-white text-primary-600 font-medium rounded text-sm">{language === 'en' ? 'View Tours' : 'Xem Tour'}</Link>
                        </div>
                    )
                }
                if (section.type === 'newsletter') {
                    return (
                        <div key={section.id} className="bg-white rounded-lg shadow-sm p-4">
                            <h3 className="font-semibold text-sm mb-2">{language === 'en' ? 'Newsletter' : 'Nhận tin'}</h3>
                            <input type="email" placeholder="Email" className="w-full px-3 py-1.5 border rounded text-sm mb-2" />
                            <button className="w-full px-3 py-1.5 bg-primary-600 text-white rounded text-sm font-medium">{language === 'en' ? 'Subscribe' : 'Đăng ký'}</button>
                        </div>
                    )
                }
                return null
            })}
        </div>
    )

    return (
        <>
            <Helmet>
                <title>{title} | Phú Quốc Travel</title>
                <meta name="description" content={excerpt} />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Hero - Overlay Style */}
                {config.hero_style === 'overlay' && (
                    <div className="relative h-[35vh] md:h-[45vh] overflow-hidden">
                        {article.featured_image ? (
                            <img src={article.featured_image} alt={title} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-800" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                        <div className="absolute inset-0 flex items-end">
                            <div className="w-full px-4 pb-4 md:pb-6">
                                <div className="max-w-4xl mx-auto">
                                    <nav className="flex items-center gap-1 text-xs text-white/70 mb-2">
                                        <Link to="/" className="hover:text-white"><Home className="w-3 h-3" /></Link>
                                        <ChevronRight className="w-3 h-3" />
                                        <Link to="/blog" className="hover:text-white">Blog</Link>
                                        <ChevronRight className="w-3 h-3" />
                                        <span className="text-white/90">{getCatName(currentCategory)}</span>
                                    </nav>
                                    <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 leading-tight">{title}</h1>
                                    <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs text-white/80">
                                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{article.author_name || 'Admin'}</span>
                                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(article.published_at || article.created_at || '', language)}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.read_time || '5 min'}</span>
                                        {article.views && <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.views}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Hero - Above Style */}
                {config.hero_style === 'above' && (
                    <>
                        {article.featured_image && (
                            <div className="h-[30vh] md:h-[40vh] overflow-hidden">
                                <img src={article.featured_image} alt={title} className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="px-4 py-4 md:py-6">
                            <div className="max-w-4xl mx-auto">
                                <nav className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                    <Link to="/" className="hover:text-primary-600"><Home className="w-3 h-3" /></Link>
                                    <ChevronRight className="w-3 h-3" />
                                    <Link to="/blog" className="hover:text-primary-600">Blog</Link>
                                    <ChevronRight className="w-3 h-3" />
                                    <span>{getCatName(currentCategory)}</span>
                                </nav>
                                <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h1>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                    <span className="flex items-center gap-1"><User className="w-4 h-4" />{article.author_name || 'Admin'}</span>
                                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(article.published_at || article.created_at || '', language)}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Hero - Minimal Style */}
                {config.hero_style === 'minimal' && (
                    <div className="px-4 pt-4 md:pt-6">
                        <div className="max-w-3xl mx-auto">
                            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 mb-3">
                                <ArrowLeft className="w-4 h-4" /> {language === 'en' ? 'Back' : 'Quay lại'}
                            </Link>
                            <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h1>
                            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-4">
                                <span className="flex items-center gap-1"><User className="w-4 h-4" />{article.author_name || 'Admin'}</span>
                                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(article.published_at || article.created_at || '', language)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="px-4 py-4 md:py-6">
                    <div className={`${showSidebar ? 'max-w-5xl mx-auto lg:grid lg:grid-cols-3 lg:gap-6' : 'max-w-3xl mx-auto'}`}>
                        <div className={showSidebar ? 'lg:col-span-2' : ''}>
                            {config.hero_style === 'minimal' && article.featured_image && (
                                <img src={article.featured_image} alt={title} className="w-full rounded-lg mb-4 shadow-sm" />
                            )}

                            {/* Article Content */}
                            <article className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4">
                                <div className="prose-content max-w-none"
                                    dangerouslySetInnerHTML={{
                                        __html: (content || '')
                                            // Handle escaped newlines (legacy markdown content)
                                            .replace(/\\n\\n/g, '</p><p>')
                                            .replace(/\\n/g, '<br/>')
                                            // Handle markdown headings if not already HTML
                                            .replace(/^# (.*$)/gm, '<h1>$1</h1>')
                                            .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                                            .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                                            // Handle markdown bold/italic if not already HTML
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                            // Handle markdown lists
                                            .replace(/^- (.*$)/gm, '<li>$1</li>')
                                            // Wrap plain text paragraphs
                                            .replace(/\n\n/g, '</p><p>')
                                    }}
                                />

                                {/* Share icons */}
                                {config.show_share && (
                                    <div className="flex items-center gap-2 pt-6 mt-6 border-t">
                                        <span className="text-sm text-gray-500 mr-2">{language === 'en' ? 'Share:' : 'Chia sẻ:'}</span>
                                        <button onClick={() => handleShare('facebook')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition" title="Facebook">
                                            <Facebook className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleShare('twitter')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition" title="Twitter">
                                            <Twitter className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleShare('email')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition" title="Email">
                                            <Mail className="w-4 h-4" />
                                        </button>
                                        <button onClick={handleCopyLink} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition" title={copied ? 'Copied!' : 'Copy link'}>
                                            {copied ? <Check className="w-4 h-4 text-green-500" /> : <LinkIcon className="w-4 h-4" />}
                                        </button>
                                        <button onClick={() => setSaved(!saved)} className={`p-2 rounded-full transition ${saved ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} title={saved ? 'Saved' : 'Save'}>
                                            <Bookmark className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
                                        </button>
                                    </div>
                                )}
                            </article>

                            {/* Comments */}
                            {config.show_comments && (
                                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4">
                                    <h3 className="font-semibold flex items-center gap-2 mb-4 text-lg">
                                        <MessageCircle className="w-5 h-5 text-primary-500" />
                                        {language === 'en' ? `Comments (${comments.length})` : `Bình luận (${comments.length})`}
                                    </h3>

                                    {/* Comment Form */}
                                    <form onSubmit={handleSubmitComment} className="mb-6 p-4 bg-gray-50 rounded-lg">
                                        {!user && (
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <input
                                                    type="text"
                                                    placeholder={language === 'en' ? 'Your name' : 'Tên của bạn'}
                                                    value={commentForm.name}
                                                    onChange={e => setCommentForm({ ...commentForm, name: e.target.value })}
                                                    className="px-3 py-2 border rounded-lg text-sm"
                                                    required
                                                />
                                                <input
                                                    type="email"
                                                    placeholder="Email"
                                                    value={commentForm.email}
                                                    onChange={e => setCommentForm({ ...commentForm, email: e.target.value })}
                                                    className="px-3 py-2 border rounded-lg text-sm"
                                                    required
                                                />
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder={language === 'en' ? 'Write a comment...' : 'Viết bình luận...'}
                                                value={commentForm.content}
                                                onChange={e => setCommentForm({ ...commentForm, content: e.target.value })}
                                                className="flex-1 px-4 py-2 border rounded-lg text-sm"
                                                required
                                            />
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                                            >
                                                <Send className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {submitSuccess && (
                                            <p className="text-green-600 text-sm mt-2">
                                                {language === 'en' ? 'Comment submitted for review!' : 'Bình luận đã gửi để duyệt!'}
                                            </p>
                                        )}
                                    </form>

                                    {/* Comments List */}
                                    {comments.length > 0 ? (
                                        <div className="space-y-4">
                                            {comments.map(comment => (
                                                <div key={comment.id} className="border-b pb-4 last:border-0">
                                                    <div className="flex gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                                                            {(comment.user_name || comment.guest_name || 'A')[0].toUpperCase()}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-semibold text-sm">{comment.user_name || comment.guest_name}</span>
                                                                <span className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleDateString()}</span>
                                                            </div>
                                                            <p className="text-sm text-gray-700">{comment.content}</p>
                                                            <div className="flex items-center gap-3 mt-2">
                                                                <button className="text-xs text-gray-400 hover:text-primary-600 flex items-center gap-1">
                                                                    <ThumbsUp className="w-3 h-3" /> {comment.likes || 0}
                                                                </button>
                                                            </div>

                                                            {/* Replies */}
                                                            {comment.replies && comment.replies.length > 0 && (
                                                                <div className="mt-3 pl-4 border-l-2 border-gray-100 space-y-3">
                                                                    {comment.replies.map(reply => (
                                                                        <div key={reply.id} className="flex gap-2">
                                                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-bold">
                                                                                {(reply.user_name || reply.guest_name || 'A')[0].toUpperCase()}
                                                                            </div>
                                                                            <div>
                                                                                <span className="font-semibold text-xs">{reply.user_name || reply.guest_name}</span>
                                                                                <p className="text-xs text-gray-600">{reply.content}</p>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 text-gray-400 text-sm">
                                            {language === 'en' ? 'No comments yet. Be the first!' : 'Chưa có bình luận. Hãy là người đầu tiên!'}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Mobile Sidebar - shown at bottom on mobile */}
                            {showSidebar && (
                                <div className="lg:hidden mt-4">
                                    {renderSidebar()}
                                </div>
                            )}
                        </div>

                        {/* Desktop Sidebar */}
                        {showSidebar && (
                            <aside className="hidden lg:block">
                                {renderSidebar()}
                            </aside>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
